/**
 * AI breakdown tier availability (SPEC.md §6.2, §6.3, §11 item 9b).
 *
 * `fitsBudget` mirrors `parse_worker.fits_budget` in the backend exactly — but this copy is
 * a UI hint only, never authoritative. It decides which tier the lightbulb defaults to and
 * which flyout items get a disabled/blocked look; the server independently re-checks the
 * budget before ever calling a model (`services/breakdown.py::check_budget`) and returns a
 * clear 409 if the two disagree, e.g. because colossus flipped the GPU mode in the gap
 * between this fetch and the click. This is why staying in sync with the backend copy isn't
 * safety-critical — §6.3's budget table is stable spec text, not something that changes
 * silently, and worst case the hint is wrong for one click.
 */

import { useEffect, useMemo, useState } from "react";
import { ApiError, api, type Health, type ModelSlots, type Tier, type VramClass } from "./api";

export const TIER_ORDER: readonly Tier[] = ["bright", "on", "dim"];
/** Highest first — this is display order for the flyout, not the auto-default. See
 * `DEFAULT_TIER_PREFERENCE` for that. */

const DEFAULT_TIER_PREFERENCE: readonly Tier[] = ["on", "bright", "dim"];
/** "On" is the tier every blurb already calls "the default" (`TIER_BLURB.on`, below, and
 * `CaptureTierPicker.tsx`'s own copy) — this is what actually makes that true. Picking the
 * *highest* available tier here, as `TIER_ORDER` does for the flyout, silently ran Bright
 * on every account that happened to have it assigned, just because nobody had opened the
 * flyout to pick something cheaper (D198: "many are doing overkill and trying to do
 * Bright" was people clicking the lit main face, not actually choosing Bright). Bright and
 * Dim both stay one click away in the flyout for when they're genuinely wanted. */

export const TIER_LABEL: Record<Tier, string> = { dim: "Dim", on: "On", bright: "Bright" };

export const TIER_BLURB: Record<Tier, string> = {
  dim: "Fast, always available, rougher",
  on: "The default. Better, may need the GPU",
  bright: "Best. Slowest or costliest, worth it when it matters",
};

export function fitsBudget(vramClass: VramClass, mode: string): boolean {
  if (vramClass === "none") return true;
  if (mode === "free") return true;
  if (mode === "shared") return vramClass === "light";
  return false;
}

export type AiTierAvailability = {
  /** `EFSS_AI_ENABLED` (§6.2) — the control doesn't render at all when this is false. */
  aiEnabled: boolean;
  /** The highest tier that's both assigned and currently within GPU budget, or `null` if
   * none is — the lightbulb's default click target and its "off" state. */
  defaultTier: Tier | null;
  available: Record<Tier, boolean>;
  /** Why a tier that *isn't* available is blocked — unassigned vs. GPU budget — for the
   * flyout's hover text. Absent for a tier that's available. */
  blockedReason: Partial<Record<Tier, string>>;
};

/** The default for any `TaskRow` that isn't handed a live value — most are, via `TaskList`
 * (below), but the many direct `<TaskRow>` renders in tests aren't, and shouldn't have to
 * be just to get a component that doesn't render the AI button at all. */
export const NO_AI_TIERS: AiTierAvailability = {
  aiEnabled: false,
  defaultTier: null,
  available: { dim: false, on: false, bright: false },
  blockedReason: {},
};

export function computeAvailability(health: Health | null, slots: ModelSlots | null): AiTierAvailability {
  if (!health || !health.ai_enabled || !slots) return NO_AI_TIERS;

  const available = {} as Record<Tier, boolean>;
  const blockedReason: Partial<Record<Tier, string>> = {};
  for (const tier of TIER_ORDER) {
    const slot = slots[tier];
    if (!slot) {
      available[tier] = false;
      blockedReason[tier] = "No model is assigned to this tier (AI page).";
      continue;
    }
    const fits = fitsBudget(slot.vram_class, health.gpu_mode);
    available[tier] = fits;
    if (!fits) {
      blockedReason[tier] = `GPU mode is "${health.gpu_mode}" right now - ${slot.name} doesn't fit.`;
    }
  }

  const defaultTier = DEFAULT_TIER_PREFERENCE.find((t) => available[t]) ?? null;
  return { aiEnabled: true, defaultTier, available, blockedReason };
}

/** Fetched once per mount, same as `useModelConfigs` — a small independent fetch per
 * `TaskList` instance costs nothing a solo user would notice, and GPU mode changing from an
 * external actor (colossus) mid-session is already handled by the server being authoritative
 * (see the module docstring), not by polling here. */
export function useAiTierAvailability(): AiTierAvailability {
  const [health, setHealth] = useState<Health | null>(null);
  const [slots, setSlots] = useState<ModelSlots | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [h, s] = await Promise.all([api.health(), api.modelSlots()]);
        if (!cancelled) {
          setHealth(h);
          setSlots(s);
        }
      } catch (err) {
        // A read-only hint: if it can't be fetched (offline, no admin scope), the control
        // just renders unavailable rather than surfacing an error of its own.
        if (!(err instanceof ApiError)) throw err;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return useMemo(() => computeAvailability(health, slots), [health, slots]);
}
